import { useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { GenderPatchModelInput, StatusModel, GenderModel } from '@utmost.co/enterprise-models';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';

const PATCH_GENDER = gql`
  mutation PatchGender($uid: String!, $model: GenderPatchModelInput!) {
    patchGender(uid: $uid, model: $model) {
      uid
    }
  }
`;

const DEACTIVATE_GENDER = gql`
  mutation DeactivateGender($uid: String!) {
    deactivateGender(uid: $uid) {
      uid
    }
  }
`;

const ACTIVATE_GENDER = gql`
  mutation ActivateGender($uid: String!) {
    activateGender(uid: $uid) {
      uid
    }
  }
`;

interface Output {
  patchGender: (uid: string, model: Partial<GenderPatchModelInput>) => Promise<void>;
  loading: boolean;
  changeStatus: (uid: string, status: StatusModel) => Promise<void>;
}

export const usePatchGenders: () => Output = () => {
  const [patchGenderMutation, { loading }] = useMutation<
    { patchGender: DeepPartial<GenderModel> },
    { uid: string; model: Partial<GenderPatchModelInput> }
  >(PATCH_GENDER);
  const [deactivateGender] = useMutation<{ deactivateGender: DeepPartial<GenderModel> }, { uid: string }>(
    DEACTIVATE_GENDER
  );
  const [activateGender] = useMutation<{ activateGender: DeepPartial<GenderModel> }, { uid: string }>(ACTIVATE_GENDER);
  const { showError } = useSnackbar();

  const patchGender = useCallback(
    async (uid: string, model: Partial<GenderPatchModelInput>) => {
      try {
        await patchGenderMutation({ variables: { uid, model } });
      } catch (e: any) {
        showError(e.message);
      }
    },
    [showError, patchGenderMutation]
  );

  const changeStatus = useCallback(
    async (uid: string, status: StatusModel) => {
      try {
        if (status === 'ACTIVE') {
          activateGender({ variables: { uid } });
        } else if (status === 'INACTIVE') {
          deactivateGender({ variables: { uid } });
        } else {
          throw new Error(`Unable to change status to ${status}`);
        }
      } catch (e: any) {
        showError(e.message);
      }
    },
    [activateGender, deactivateGender, showError]
  );
  return {
    patchGender,
    loading,
    changeStatus
  };
};
